import { Button, Stack } from '@mui/material';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DialogApi } from '../../api/DialogApi';
import { DialogPromptStartNextLesson } from '../../api/DialogPromptApi';
import { LessonApi } from '../../api/LessonApi';
import { IUser, UserApi } from '../../api/UserApi';
import AppBarContext from '../../context/AppBarContext';
import DialogPromptFooterContext from '../../context/DialogPromptFooterContext';
import ChatBubble from '../ChatBubble';
import { handleErrorMessage } from '../ErrorDialog';
import { LastLessonOfClass, RegistrationReminder } from './DialogPromptComponent';

interface DialogPromptStartNextLessonComponentProps {
    dialogPrompt: DialogPromptStartNextLesson;
    setDialogPromptComplete: (dialogPromptId: number) => void;
    showAvatar?: boolean;
    chatBubbleContainerRef: React.RefObject<HTMLDivElement>;
    isSettings?: boolean;
}

function findNext(list: any[], currentId: number) {
    const currentDialogIndex = list.findIndex(item => item.id === currentId);
    if (currentDialogIndex == -1) {
        throw Error("Unable to find id " + currentId + " in list " + list.toString);
    }
    // Dialog ID found in the list
    if (currentDialogIndex === list.length - 1) {
        return -1
    } else {
        // Get the next dialog ID
        const nextDialogId = list[currentDialogIndex + 1].id;
        return nextDialogId
    }
}

const START_NEXT_LESSON_ROUTE_DELAY_MS = 1500;

const DialogPromptStartNextLessonComponent: React.FC<DialogPromptStartNextLessonComponentProps> =
    ({ dialogPrompt, setDialogPromptComplete, showAvatar = true, chatBubbleContainerRef }) => {

        const { setErrorMessage } = React.useContext(AppBarContext)
        const history = useHistory();
        const location = useLocation();

        const { setActionList, clearFooterList } = React.useContext(DialogPromptFooterContext)

        const [hasNextLesson, setHasNextLesson] = React.useState<boolean>(true);

        const [response, setResponse] = React.useState<string | null>(null);

        const [reminderMessage, setReminderMessage] = React.useState<string | null>(null);
        const [reminderMessageDone, setReminderMessageDone] = React.useState(false);
        const [chatBubbleDone, setChatBubbleDone] = React.useState(false);
        const [chatBubbleResponseDone, setChatBubbleResponseDone] = React.useState(false);
        const [chatBubbleLastLessonDone, setChatBubbleLastLessonDone] = React.useState(false);
        const [nextDialogUrl, setNextDialogUrl] = React.useState<string | null>(null);
        const completedRef = React.useRef(false);


        const [promptAudio] = React.useState<Blob | null | undefined>(undefined);
        const [registrationReminderAudio] = React.useState<Blob | null | undefined>(undefined);
        const [lastLessonAudio] = React.useState<Blob | null | undefined>(undefined);

        React.useEffect(() => {
            clearFooterList()

            // what reminder message do we need? (if any)
            UserApi.get()
                .then(response => {
                    // Handle the response
                    const user: IUser = response.data;
                    if (user.email === undefined || user.email === null || user.read_only_email_validated == false) {
                        setReminderMessage(RegistrationReminder);
                    } else {
                        setReminderMessageDone(true);
                    }
                })
                .catch(err => {
                    setReminderMessageDone(true);
                    handleErrorMessage(err, setErrorMessage, history, location);
                });
        }, [clearFooterList, history, location, setErrorMessage]);

        const handleYesButtonClick = React.useCallback(async () => {
            setResponse(dialogPrompt.yes_response);
            clearFooterList();

            try {
                // Get current dialog
                const dialogResponse = await DialogApi.getById(dialogPrompt.dialog_id);
                const dialog = dialogResponse.data;

                // Get all dialogs in current lesson
                const dialogListResponse = await DialogApi.getAll(dialog.lesson_id);
                const dialogList = dialogListResponse.data.filter(d => !d.is_hidden);

                // Find next dialog in current lesson
                let nextDialogId = findNext(dialogList, dialogPrompt.dialog_id);
                let nextLessonId: number | null = null;
                let courseId = -1;
                let lessonId = dialog.lesson_id;

                if (nextDialogId < 0) {
                    // Last dialog in lesson; find next lesson
                    const lessonResponse = await LessonApi.getById(dialog.lesson_id);
                    const lesson = lessonResponse.data;
                    courseId = lesson.course_id;

                    const lessonListResponse = await LessonApi.getAll(courseId);
                    const lessonList = lessonListResponse.data.filter(l => !l.is_hidden);

                    nextLessonId = findNext(lessonList, dialog.lesson_id);

                    if (nextLessonId !== null && nextLessonId >= 0) {
                        // Get first dialog of next lesson
                        const nextDialogListResponse = await DialogApi.getAll(nextLessonId);
                        const nextDialogList = nextDialogListResponse.data.filter(d => !d.is_hidden);

                        if (nextDialogList.length > 0) {
                            nextDialogId = nextDialogList[0].id;
                            lessonId = nextLessonId;
                        } else {
                            nextDialogId = -1;
                        }
                    }
                }

                if (nextDialogId >= 0) {
                    // Get courseId if it wasn't already fetched (still in same lesson)
                    if (courseId === -1) {
                        const lessonResponse = await LessonApi.getById(lessonId);
                        courseId = lessonResponse.data.course_id;
                    }

                    setNextDialogUrl(`/lesson/${courseId}/${lessonId}/${nextDialogId}`);
                    setChatBubbleLastLessonDone(true);
                } else {
                    // No more dialogs or lessons
                    setHasNextLesson(false);
                }
            } catch (err) {
                setChatBubbleLastLessonDone(true);
                console.error("Error getting lesson: ", err);
                handleErrorMessage(err, setErrorMessage, history, location);
            }
        }, [dialogPrompt.yes_response, dialogPrompt.dialog_id, clearFooterList, setErrorMessage, history, location]);

        const handleNoButtonClick = React.useCallback(() => {
            setResponse(dialogPrompt.no_response);
            setDialogPromptComplete(dialogPrompt.id)
            clearFooterList();
        }, [dialogPrompt.no_response, dialogPrompt.id, setDialogPromptComplete, clearFooterList]);

        React.useEffect(() => {
            if (chatBubbleDone) {
                setActionList([
                    <Button variant="contained" color="secondary" onClick={() => handleNoButtonClick()} sx={{ ml: 4 }}>
                        {dialogPrompt.no_button_text}
                    </Button>,
                    <Button variant="contained" color="primary" onClick={() => handleYesButtonClick()} sx={{ ml: 4 }}>
                        {dialogPrompt.yes_button_text}
                    </Button>
                ])
            }
        }, [chatBubbleDone, dialogPrompt.no_button_text, dialogPrompt.yes_button_text, handleNoButtonClick, handleYesButtonClick, setActionList]);

        // const handleYesButtonClick = async () => {
        //     setResponse(dialogPrompt.yes_response);
        //     clearFooterList();

        //     try {
        //         // get dialog
        //         const dialogResponse = await DialogApi.getById(dialogPrompt.dialog_id);
        //         const dialog = dialogResponse.data;

        //         // get all dialogs in this lesson
        //         const dialogListResponse = await DialogApi.getAll(dialog.lesson_id);
        //         const dialogList = dialogListResponse.data.filter(dialog => dialog.is_hidden === false);

        //         // get next dialog id
        //         let nextDialogId = findNext(dialogList, dialogPrompt.dialog_id);
        //         if (nextDialogId < 0) {
        //             // we are in last dialog of lesson

        //             // get lesson
        //             const lessonResponse = await LessonApi.getById(dialog.lesson_id);
        //             const lesson = lessonResponse.data;

        //             // get all lessons in course
        //             const lessonListResponse = await LessonApi.getAll(lesson.course_id);
        //             const lessonList = lessonListResponse.data.filter(lesson => lesson.is_hidden === false);

        //             // get next lesson
        //             let nextLessonId = findNext(lessonList, dialog.lesson_id);
        //             if (nextLessonId >= 0) {
        //                 // get first dialog of next lesson
        //                 const nextDialogListResponse = await DialogApi.getAll(nextLessonId);
        //                 const nextDialogList = nextDialogListResponse.data.filter(dialog => dialog.is_hidden === false);
        //                 nextDialogId = nextDialogList[0].id
        //             }
        //         }

        //         if (nextDialogId >= 0) {
        //             setNextDialogUrl(`/lesson?dialogId=${nextDialogId}`)
        //             setChatBubbleLastLessonDone(true)
        //         } else {
        //             setHasNextLesson(false); // changing from true -> false will trigger last lesson chat bubble below
        //         }
        //     } catch (err) {
        //         setChatBubbleLastLessonDone(true)
        //         console.error("Error getting lesson" + err)
        //         handleErrorMessage(err, setErrorMessage, history, location);
        //     }
        // }

        React.useEffect(() => {
            if (
                chatBubbleResponseDone &&
                chatBubbleLastLessonDone &&
                hasNextLesson == false &&
                !completedRef.current
            ) {
                completedRef.current = true;
                // Only complete the dialog if there is no next lesson, and then summary will display and ask user.
                setDialogPromptComplete(dialogPrompt.id)
            }
        }, [chatBubbleResponseDone, chatBubbleLastLessonDone, hasNextLesson, dialogPrompt.id, setDialogPromptComplete])

        React.useEffect(() => {
            if (chatBubbleResponseDone && chatBubbleLastLessonDone && nextDialogUrl) {
                // If there is a next lesson, do not complete the dialog so the following summary does not run.
                const timeoutId = window.setTimeout(() => {
                    history.push(nextDialogUrl);
                }, START_NEXT_LESSON_ROUTE_DELAY_MS);
                return () => window.clearTimeout(timeoutId);
            }
        }, [chatBubbleResponseDone, chatBubbleLastLessonDone, nextDialogUrl, history])

        return (
            <Stack>
                {reminderMessage && (
                    <ChatBubble
                        isTeacher={true}
                        message={reminderMessage}
                        messageAudio={registrationReminderAudio ? registrationReminderAudio : null}
                        showAvatar={showAvatar}
                        setChatBubbleDone={setReminderMessageDone}
                        chatBubbleContainerRef={chatBubbleContainerRef}
                    />
                )}
                {reminderMessageDone && (
                    <ChatBubble
                        isTeacher={dialogPrompt.is_teacher}
                        message={dialogPrompt.prompt}
                        messageAudio={promptAudio ? promptAudio : null}
                        showAvatar={showAvatar}
                        setChatBubbleDone={setChatBubbleDone}
                        chatBubbleContainerRef={chatBubbleContainerRef}
                    />
                )}
                {response ? (
                    <ChatBubble
                        // yes_response / no_response are authored as the teacher's closing line
                        // ("No problem. Take a break and come back whenever you are ready."), so
                        // they must render as the teacher, not as the student's reply.
                        isTeacher={dialogPrompt.is_teacher}
                        message={response}
                        messageAudio={null} // audio not pre-fetched for the closing line
                        showAvatar={showAvatar}
                        setChatBubbleDone={setChatBubbleResponseDone}
                        chatBubbleContainerRef={chatBubbleContainerRef}
                    />
                ) : null}
                {hasNextLesson == false && chatBubbleResponseDone ? (
                    <ChatBubble
                        isTeacher={true}
                        message={LastLessonOfClass}
                        messageAudio={lastLessonAudio ? lastLessonAudio : null}
                        showAvatar={showAvatar}
                        setChatBubbleDone={setChatBubbleLastLessonDone}
                        chatBubbleContainerRef={chatBubbleContainerRef}
                    />
                ) : null}

            </Stack>
        );
    };

export default DialogPromptStartNextLessonComponent;
